import React, { useMemo, useState, useEffect } from 'react';
import { MaintenanceMenu } from '../../../API/GoMoto';
import { MaintenanceMenuType } from '../../../API/Enums';
import { useLocale, useLocalization } from '../../../context/Localization/localization';
import { BackgroundDispatchTypes, useBackgroundDataDispatch, useBackgroundDataState } from '../../../context/backgroundData';
import { KioskAwareControl } from '../../../controls/AccessControl/KioskAwareControl';
import { SpgPriceText } from '../../../controls/SpgPriceText/SpgPriceText';
import css from './MaintenanceSchedules.module.scss';
import classNames from 'classnames';
import { Spinner } from '../../../controls/Spinner/Spinner';
import { Types, useCart, useCartDispatch } from '../../../context/cart';
import { GMCheckbox } from '../../../controls/Checkbox';
import { GMSelect } from '../../../controls/Select';
import { GMGenericButton } from '../../../controls/ButtonBase';
import { Locales } from '../../../context/Localization/localization.types';

export const MaintenanceSchedules: React.FC = () => {
    const Localization = useLocalization();

    return (
        <>
            <div className={css.helperText}>{Localization.MaintenanceScheduleDescription}</div>
            <KioskAwareControl kioskComponent={<KioskComponent />} webComponent={<WebComponent />} />
        </>
    );
};

const KioskComponent: React.FC = () => {
    const { loadingMenus } = useBackgroundDataState();

    return (
        <>
            <div className={css.grid}>
                <div className={css.column}>
                    <OptionControl type={MaintenanceMenuType.Schedule} />
                    {!loadingMenus && (
                        <div className={classNames(css.optionsBackground, css.maintScheduleBackground)}>
                            <div className={css.optionsContainer}>
                                <OptionList type={MaintenanceMenuType.Schedule} />
                            </div>
                        </div>
                    )}
                </div>
                <div className={css.column}>
                    <OptionControl type={MaintenanceMenuType.Mileage} />
                    {!loadingMenus && (
                        <div className={classNames(css.optionsBackground, css.mileageBackground)}>
                            <div className={css.optionsContainer}>
                                <OptionList type={MaintenanceMenuType.Mileage} />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {loadingMenus && (
                <div className={css.loading}>
                    <Spinner />
                </div>
            )}
        </>
    );
};

const WebComponent: React.FC = () => {
    const { loadingMenus } = useBackgroundDataState();

    return (
        <div>
            <OptionControl type={MaintenanceMenuType.Schedule} />
            <OptionControl type={MaintenanceMenuType.Mileage} />
            {loadingMenus ? (
                <Spinner />
            ) : (
                <>
                    <OptionList type={MaintenanceMenuType.Mileage} />
                    <OptionList type={MaintenanceMenuType.Schedule} />
                </>
            )}
        </div>
    );
};

interface IOptionControlProps {
    type: MaintenanceMenuType;
}

const OptionControl: React.FC<IOptionControlProps> = (props) => {
    const { scheduleOptions, mileageOptions, selectedMileageOption, selectedScheduleOption } = useBackgroundDataState();
    const backgroundDataDispatch = useBackgroundDataDispatch();

    const [value, setValue] = useState<string>(props.type === MaintenanceMenuType.Schedule ? selectedScheduleOption ?? '' : selectedMileageOption ?? '');
    const Localization = useLocalization();
    const options = props.type === MaintenanceMenuType.Schedule ? scheduleOptions : mileageOptions;

    /*
     * Since the list of mileage options are determined by the selectedSchedule option we
     * clear out the selectedMileage option when the schedule option changes.
     * We need to update the internal state value with these changes
     */
    useEffect(() => {
        if (props.type === MaintenanceMenuType.Mileage) {
            setValue(selectedMileageOption ?? '');
        }
    }, [selectedMileageOption, props.type]);

    const items = useMemo(
        () =>
            options.map((option) => ({
                label: option.label,
                value: option.id,
            })),
        [options],
    );

    const onChange = (newValue: string) => {
        setValue(newValue);

        backgroundDataDispatch({
            type: props.type === MaintenanceMenuType.Schedule ? BackgroundDispatchTypes.SetSelectedScheduleOption : BackgroundDispatchTypes.SetSelectedMileageOption,
            payload: newValue,
        });
    };

    return (
        <div className={options.length === 0 ? css.hidden : ''}>
            <div className={css.selectLabel}>
                {options.length > 1 ? (props.type === MaintenanceMenuType.Schedule ? Localization.ChooseScheduleOption : Localization.ChooseMileageOption) : ''}
            </div>
            <GMSelect
                id={Localization.MaintenanceSchedules}
                selectRootClass={css.select}
                value={value}
                items={items}
                onChange={onChange}
                menuItemRootClass={css.selectMenuItem}
            />
        </div>
    );
};

interface IOptionListProps {
    type: MaintenanceMenuType;
}

const OptionList: React.FC<IOptionListProps> = (props) => {
    const { menus } = useBackgroundDataState();

    const options = useMemo(() => menus.filter((m) => m.type === props.type), [menus, props.type]);

    return (
        <div className={css.options}>
            {options.map((m, index) => (
                <MaintenanceMenuCard maintenance={m} key={`menu-card-${index}`} />
            ))}
        </div>
    );
};

interface IMaintenanceMenuCardProps {
    maintenance: MaintenanceMenu;
}

const MaintenanceMenuCard: React.FC<IMaintenanceMenuCardProps> = (props) => {
    const Localization = useLocalization();
    const locale = useLocale();
    const cart = useCart();
    const cartDispatch = useCartDispatch();

    return (
        <div className={css.maintMenuCard}>
            <GMGenericButton
                onClick={() => {
                    cartDispatch({
                        type: Types.ToggleMaintMenu,
                        payload: props.maintenance,
                    });
                }}
                id={Localization.ToggleMaintenaceMenu}
                ariaLabel={Localization.ToggleMaintenaceMenu}
                ariaChecked={!!(cart.maintMenus.findIndex((m) => props.maintenance.id === m.id && props.maintenance.scheduleOption === m.scheduleOption) > -1)}
            >
                <div className={css.maintMenuCardContent}>
                    <div className={css.header}>
                        <div className={css.title}>{props.maintenance.title}</div>
                        <div>
                            <GMCheckbox
                                checked={
                                    !!(cart.maintMenus.findIndex((m) => props.maintenance.id === m.id && props.maintenance.scheduleOption === m.scheduleOption) > -1)
                                }
                                iconSize={'medium'}
                            />
                        </div>
                    </div>
                    <div className={css.numberIncluded}>
                        {props.maintenance.opDescriptions.length === 0
                            ? Localization.MaintenanceEmpty
                            : Localization.formatString(
                                Localization.MaintenanceIncludes,
                                props.maintenance.opDescriptions.length.toString(),
                                props.maintenance.opDescriptions.length > 1 && locale === Locales.en ? 's' : '',
                            )}
                    </div>
                    <div className={css.description}>
                        {props.maintenance.opDescriptions.map((description: string, index: number) => (
                            <div key={`menu-${props.maintenance.id}-op-${index}`}>{description}</div>
                        ))}
                    </div>
                    <div className={css.price}>
                        <SpgPriceText
                            priceLow={props.maintenance.priceLow}
                            priceHigh={props.maintenance.priceHigh}
                            spgPricing={props.maintenance.spgPricing}
                            showSingleWhenNoRange={false}
                        />
                    </div>
                </div>
            </GMGenericButton>
        </div>
    );
};
